import {
	type CSSProperties,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	BUILDINGS,
	CONTOURS,
	PLACE_LABELS,
	ROADS_MAIN,
	ROADS_SERVICE,
	SUMMIT,
	formatClockSpan,
	formatWeekday,
	getVenuePinData,
	getVenueSpanMeters,
	type ProgramEvent,
	type VenuePin,
} from '#app/domains/program/pure'
import { getGoogleMapsDirectionsUrl } from '#app/utils/maps.ts'
import {
	prefersReducedMotion,
	useInViewport,
	useRevealOnce,
} from '#app/utils/motion.ts'

const COASTLINE = CONTOURS.find((band) => band.level === 0)
const DEPTHS = CONTOURS.filter((band) => band.level < 0).sort(
	(a, b) => b.level - a.level,
)
const RELIEF = CONTOURS.filter((band) => band.level > 0)

// square crop around the venues; `slice` turns it into a landscape strip on
// phones and a portrait panel in the sticky desktop column
const VIEW = { x: 10, y: 40, w: 330, h: 330 }

/** seconds the walker lingers at each house, and its pace (units/s) */
const WALK = { hold: 1.4, speed: 38 }

const isAddress = (location: string) => /\d/.test(location)

const directionsUrl = (location: string) =>
	getGoogleMapsDirectionsUrl(
		/4265/.test(location) ? location : `${location}, 4265 Håvik`,
	)

type Stop = VenuePin & { events: ProgramEvent[] }

/**
 * The program on a night-time map of Vorrå (real OpenStreetMap geometry):
 * roads draw themselves in, the venue houses light up in program order and a
 * lantern walks the route from concert to concert, lingering at each door.
 * The venue of the active concert is highlighted, with a callout.
 */
export function VenueNightMap({
	events,
	activeEventId,
	onActivate,
}: {
	events: ProgramEvent[]
	activeEventId?: string | null
	/** a venue was pointed at: activate its first concert */
	onActivate?: (eventId: string) => void
}) {
	const uid = useId().replace(/:/g, '')
	const [figureRef, reveal] = useRevealOnce<HTMLElement>({ threshold: 0.3 })
	const [svgWrapRef, inView] = useInViewport<HTMLDivElement>('0px')
	const svgRef = useRef<SVGSVGElement>(null)
	const canvasSize = useElementSize(svgWrapRef)

	const stops: Stop[] = useMemo(
		() =>
			getVenuePinData(events).map((pin) => ({
				...pin,
				events: events.filter((event) => event.location?.trim() === pin.source),
			})),
		[events],
	)
	const placed = stops.filter((stop) => stop.precision !== 'unknown')
	const span = getVenueSpanMeters(stops)
	const activeEvent = events.find((event) => event.id === activeEventId)
	const activeStop = stops.find(
		(stop) => stop.source === activeEvent?.location?.trim(),
	)

	const route = placed.length > 1 ? routeGeometry(placed) : null

	// SMIL animations (the walker) have their own clock: pause it when the
	// map is off screen, and never run it for reduced motion
	useEffect(() => {
		const svg = svgRef.current
		if (!svg?.pauseAnimations) return
		if (inView && !prefersReducedMotion()) svg.unpauseAnimations()
		else svg.pauseAnimations()
	}, [inView])

	return (
		<figure
			ref={figureRef}
			className="nm"
			data-reveal={reveal}
			data-paused={inView ? undefined : ''}
			aria-labelledby={`${uid}-caption`}
		>
			<div ref={svgWrapRef} className="nm-canvas">
				<svg
					ref={svgRef}
					className="nm-svg"
					viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
					preserveAspectRatio="xMidYMid slice"
					role="img"
					aria-label={`Kart over Vorrå med ${stops.length} ${stops.length === 1 ? 'konsertstad' : 'konsertstader'}`}
				>
					<defs>
						<radialGradient id={`${uid}-glow`}>
							<stop offset="0" stopColor="#f6d77a" stopOpacity="0.9" />
							<stop offset="0.35" stopColor="#f2a043" stopOpacity="0.35" />
							<stop offset="1" stopColor="#f2a043" stopOpacity="0" />
						</radialGradient>
						<path id={`${uid}-coast`} d={COASTLINE?.d} pathLength={1} />
					</defs>

					<rect
						className="nm-land"
						x={VIEW.x - 40}
						y={VIEW.y - 60}
						width={VIEW.w + 80}
						height={VIEW.h + 120}
					/>
					{/* Kartverket terrain: the sea, darker with depth, isobaths… */}
					<use href={`#${uid}-coast`} className="nm-water" />
					{DEPTHS.map((band, i) => (
						<path
							key={band.level}
							className="nm-depth"
							d={band.d}
							pathLength={1}
							style={{ '--nm-i': i } as CSSProperties}
						/>
					))}
					<g className="nm-waves">
						{[268, 284, 300, 318].map((y, i) => (
							<path
								key={y}
								d={`M${40 + i * 18} ${y} q 14 -3 28 0 t 28 0`}
								style={{ '--nm-i': i } as CSSProperties}
							/>
						))}
					</g>
					{/* …and the land in 5 m terraces */}
					{RELIEF.map((band, i) => (
						<path
							key={band.level}
							className="nm-relief"
							data-index={band.level % 10 === 0 ? '' : undefined}
							d={band.d}
							pathLength={1}
							style={{ '--nm-i': i } as CSSProperties}
						/>
					))}
					<use href={`#${uid}-coast`} className="nm-coast" />
					<path className="nm-buildings" d={BUILDINGS} />
					<path
						className="nm-road nm-road-service"
						d={ROADS_SERVICE}
						pathLength={1}
					/>
					<path
						className="nm-road nm-road-main"
						d={ROADS_MAIN}
						pathLength={1}
					/>

					<text
						className="nm-label nm-label-road"
						x="42"
						y="128"
						transform="rotate(65 42 128)"
					>
						Vorråvegen
					</text>
					<text
						className="nm-label nm-label-road"
						x="150"
						y="212"
						transform="rotate(14 150 212)"
					>
						Liarvegen
					</text>
					<text className="nm-label nm-label-water" x="112" y="288">
						Vorråstranda
					</text>
					{PLACE_LABELS.map((place) => (
						<text
							key={place.name}
							className="nm-label nm-label-water"
							x={place.x}
							y={place.y}
							// keep names near the edges inside the frame
							textAnchor={
								place.x > VIEW.x + VIEW.w - 60
									? 'end'
									: place.x < VIEW.x + 60
										? 'start'
										: 'middle'
							}
						>
							{place.name}
						</text>
					))}
					<g
						className="nm-summit"
						transform={`translate(${SUMMIT.x} ${SUMMIT.y})`}
					>
						<path d="M0 -3.6 L3.4 2.4 L-3.4 2.4 Z" />
						<text x="5.5" y="2.4">
							{SUMMIT.z} moh.
						</text>
					</g>

					{/* the lit houses */}
					{placed.map((stop, i) => (
						<circle
							key={`glow-${stop.number}`}
							className="nm-glow"
							cx={stop.x}
							cy={stop.y}
							r={26}
							fill={`url(#${uid}-glow)`}
							data-active={stop === activeStop ? '' : undefined}
							style={{ '--nm-i': i } as CSSProperties}
						/>
					))}

					{route ? (
						<>
							<path className="nm-route-trace" d={route.d} pathLength={1} />
							<path id={`${uid}-route`} className="nm-route" d={route.d} />
							<circle className="nm-walker" r="3.4">
								{/* fade in at the first house, out at the last, then loop */}
								<animate
									attributeName="opacity"
									values="0;1;1;0"
									keyTimes="0;0.04;0.96;1"
									dur={`${route.duration}s`}
									repeatCount="indefinite"
									begin="1.5s"
								/>
								<animateMotion
									dur={`${route.duration}s`}
									repeatCount="indefinite"
									calcMode="linear"
									keyPoints={route.keyPoints}
									keyTimes={route.keyTimes}
									begin="1.5s"
								>
									<mpath href={`#${uid}-route`} />
								</animateMotion>
							</circle>
						</>
					) : null}

					{placed.map((stop, i) => (
						<g
							key={stop.number}
							className="nm-pin"
							data-active={stop === activeStop ? '' : undefined}
							style={{ '--nm-i': i } as CSSProperties}
							onClick={() => stop.events[0] && onActivate?.(stop.events[0].id)}
						>
							<circle className="nm-pin-ring" cx={stop.x} cy={stop.y} r={10} />
							<circle className="nm-pin-dot" cx={stop.x} cy={stop.y} r={9} />
							<text className="nm-pin-number" x={stop.x} y={stop.y + 3.4}>
								{stop.number}
							</text>
							<text
								className="nm-pin-label"
								x={stop.x + stop.ldx}
								y={stop.y + stop.ldy + 2}
								textAnchor={stop.lanchor}
							>
								{stop.location}
							</text>
						</g>
					))}

					<g
						className="nm-compass"
						transform={`translate(${VIEW.x + VIEW.w - 26} ${VIEW.y + 58})`}
					>
						<circle r="10" />
						<path d="M0 -6.5 L3 4 L0 2 L-3 4 Z" />
						<text y="-13" textAnchor="middle">
							N
						</text>
					</g>
				</svg>

				{activeEvent &&
				activeStop &&
				activeStop.precision !== 'unknown' &&
				canvasSize ? (
					<div
						key={activeEvent.id}
						className="nm-callout"
						{...calloutPlacement(activeStop, canvasSize)}
						aria-hidden="true"
					>
						<span className="nm-callout-time">
							{formatWeekday(activeEvent.date)}{' '}
							{formatClockSpan(activeEvent.date, activeEvent.endsAt)}
						</span>
						<span className="nm-callout-title">{activeEvent.title}</span>
					</div>
				) : null}
			</div>

			<figcaption id={`${uid}-caption`} className="nm-caption">
				<span className="nm-caption-title">Frå stove til stove</span>
				{span
					? ` — alle scenane ligg innanfor ca. ${span} meter. Gå mellom konsertane!`
					: ' — konsertane ligg ein kort spasertur frå kvarandre.'}
				<small className="nm-credit">
					Kart: © OpenStreetMap-bidragsytarar · høgd, djupn og stadnamn: ©
					Kartverket
				</small>
			</figcaption>

			<ol className="nm-legend">
				{stops.map((stop) => (
					<li
						key={stop.number}
						className="nm-legend-item"
						data-active={stop === activeStop ? '' : undefined}
						onMouseEnter={() =>
							stop.events[0] && onActivate?.(stop.events[0].id)
						}
					>
						<span className="nm-legend-pin" aria-hidden="true">
							{stop.number}
						</span>
						<div className="nm-legend-body">
							<p className="nm-legend-name">
								{stop.location}
								{stop.precision === 'unknown' ? (
									<span className="nm-legend-note">
										{' '}
										· ikkje på kartet enno
									</span>
								) : null}
							</p>
							<p className="nm-legend-events">
								{stop.events.map((event, i) => (
									<span key={event.id}>
										{i ? ', ' : ''}
										<a
											href={`#event-${event.id}`}
											onFocus={() => onActivate?.(event.id)}
										>
											{formatWeekday(event.date)}{' '}
											{formatClockSpan(event.date, event.endsAt)}
										</a>
									</span>
								))}
							</p>
						</div>
						{isAddress(stop.source) ? (
							<a
								className="nm-legend-directions"
								href={directionsUrl(stop.source)}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={`Vegbeskriving til ${stop.location} (blir opna i Google Maps)`}
							>
								Vegbeskriving
							</a>
						) : null}
					</li>
				))}
			</ol>
		</figure>
	)
}

/** Size of an element, tracked with a ResizeObserver (null until measured). */
function useElementSize(ref: React.RefObject<HTMLElement | null>) {
	const [size, setSize] = useState<{ width: number; height: number } | null>(
		null,
	)
	useEffect(() => {
		const el = ref.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const observer = new ResizeObserver(([entry]) => {
			if (!entry) return
			const { width, height } = entry.contentRect
			setSize({ width, height })
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [ref])
	return size
}

/**
 * Where a map point lands inside the canvas. The SVG uses `slice`, so the
 * square view is scaled to cover the canvas and cropped at the edges. The
 * callout flips below the pin near the top edge and is kept inside.
 */
function calloutPlacement(
	point: { x: number; y: number },
	canvas: { width: number; height: number },
) {
	const scale = Math.max(canvas.width / VIEW.w, canvas.height / VIEW.h)
	const offsetX = (canvas.width - VIEW.w * scale) / 2
	const offsetY = (canvas.height - VIEW.h * scale) / 2
	const x = offsetX + (point.x - VIEW.x) * scale
	const y = offsetY + (point.y - VIEW.y) * scale
	const halfWidth = Math.min(104, canvas.width / 2 - 8)
	const left = Math.min(
		canvas.width - halfWidth - 8,
		Math.max(halfWidth + 8, x),
	)
	return {
		'data-side': y < 96 ? 'below' : 'above',
		style: {
			'--nm-x': `${left}px`,
			'--nm-y': `${y}px`,
			// keep the arrow on the pin when the bubble is nudged inside
			'--nm-arrow': `${x - left}px`,
		} as CSSProperties,
	}
}

/**
 * The walking route between venues (a polyline in program order) and the
 * walker's timing: travel at a steady pace, linger at each house.
 */
function routeGeometry(stops: VenuePin[]) {
	const lengths = stops
		.slice(1)
		.map((stop, i) => Math.hypot(stop.x - stops[i]!.x, stop.y - stops[i]!.y))
	const total = lengths.reduce((a, b) => a + b, 0)
	const travel = lengths.map((length) => length / WALK.speed)
	const duration = stops.length * WALK.hold + travel.reduce((a, b) => a + b, 0)

	const keyTimes: number[] = [0]
	const keyPoints: number[] = [0]
	let t = 0
	let distance = 0
	stops.forEach((_, i) => {
		t += WALK.hold
		keyTimes.push(t / duration)
		keyPoints.push(distance / total)
		if (i < lengths.length) {
			t += travel[i]!
			distance += lengths[i]!
			keyTimes.push(t / duration)
			keyPoints.push(distance / total)
		}
	})
	keyTimes[keyTimes.length - 1] = 1

	return {
		d: `M${stops.map((stop) => `${stop.x} ${stop.y}`).join(' L')}`,
		duration: duration.toFixed(2),
		keyTimes: keyTimes.map((k) => k.toFixed(4)).join(';'),
		keyPoints: keyPoints.map((k) => Math.min(1, k).toFixed(4)).join(';'),
	}
}
